"use client";

import { useRouter, useRouterState } from "@tanstack/react-router";
import NProgress from "nprogress";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { type LinkTarget } from "@/components/link";

/**
 * Pushes routes with top-loader feedback and deduplicates no-op / repeated
 * navigations. Takes a native `{ to, params, search }` target — the router
 * encodes `search` through the global codec (no hand-built URL strings).
 */
export function useNavigateWithTopLoader() {
  const router = useRouter();
  const location = useRouterState({ select: (s) => s.location });
  const pendingHrefRef = useRef<string | null>(null);

  const currentHref = useMemo(
    () => `${location.pathname}${location.searchStr ?? ""}`,
    [location.pathname, location.searchStr],
  );

  useEffect(() => {
    pendingHrefRef.current = null;
  }, [currentHref]);

  return useCallback(
    (target: LinkTarget) => {
      const built = router.buildLocation(target as never);
      const normalizedHref = `${built.pathname}${built.searchStr ?? ""}`;
      if (!normalizedHref || normalizedHref === currentHref) {
        return false;
      }
      if (pendingHrefRef.current === normalizedHref) {
        return false;
      }

      pendingHrefRef.current = normalizedHref;
      NProgress.start();
      void router.navigate(target as never);
      return true;
    },
    [currentHref, router],
  );
}
